import { Button, Clickable, H2, Hidden, Image, SpacerWrapper, Strong, TypeWrapper } from '@ahmdigital/ui';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isEmpty, isNull, join, map } from 'lodash/fp';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import { ProductType } from '../../../types/product';
import { StyledNeedsCTA } from './styles';
import constants from '../../../ahm-constants';
import CustomerType from '../../../types/customer';
import getCustomerHasChanged from '../../../utils/customer-has-changed';
import getFutureDate from '../../../utils/get-future-date';
import NeedsProductDataLoader from '../../needs-product-data-loader';
import PremiumReviewToggle from '../../premium-review-toggle-wrapper';
import PriceDisclaimer from '../../price-disclaimer';
import PricingDrawer from '../../pricing-drawer';
import productListingCommon from '../../../pages/product-listing/product-listing-common';
import ProductService from '../../../models/product';
import ProductTiles from './components/product-tiles';
import selectCustomer from '../../../selectors/select-customer';
import selectNeedsProductType from '../../../selectors/select-needs-product-type';
import selectNeedsType from '../../../selectors/select-needs-type';
import styles from './styles.module.sass';
import WrappedSection from '../wrapped-section';

type ProductsSectionProps = {
  actions?: unknown;
  compareButtonText: string;
  compareButtonToLink: string;
  customer: CustomerType;
  data?: { subtitle?: string | React.ReactNode; title: string };
  image?: { alt: string; src: React.ReactNode };
  isLoadingPrice?: boolean;
  prices?: unknown;
  productIds?: string[];
  products: {}[];
  stateProducts?: Record<string, ProductType>;
  title?: string;
  useFutureEffectiveDate?: boolean;
  withBorder?: boolean;
  withDrawer?: boolean;
  withHorizontalScroll?: boolean;
  withNeedsLink?: boolean;
  withVerticalSpacing?: boolean;
};

const ProductsSection = ({
  actions,
  compareButtonText,
  compareButtonToLink,
  customer,
  data,
  image,
  isLoadingPrice,
  prices,
  products,
  productIds: productIdsToLoad,
  stateProducts,
  title: titleFromSanity,
  useFutureEffectiveDate,
  withBorder,
  withDrawer,
  withHorizontalScroll,
  withNeedsLink,
  withVerticalSpacing,
}: ProductsSectionProps) => {
  const productIds = productIdsToLoad || map('id', products);
  const resolvedProducts = ProductService.getProducts(stateProducts, productIds);
  const productsToLoad = products || resolvedProducts;
  const productIdsHaveChanged = join('|', productIds);

  const customerHasChanged = getCustomerHasChanged(customer, useFutureEffectiveDate);
  const subtitle = data?.subtitle;
  const title = titleFromSanity || data?.title;

  const hasHeaderDetails = !isNull(image) || !isEmpty(title) || !isEmpty(subtitle);

  useEffect(() => {
    // This component is only used on pages where only 750 excess is sold
    // @ts-expect-error - Automatic, Please fix when editing this file
    actions.customer.changeHospitalExcess('750');
    // @ts-expect-error - Automatic, Please fix when editing this file
    actions.price.load(productIds, customer, { date: getFutureDate(useFutureEffectiveDate) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHasChanged, productIdsHaveChanged, useFutureEffectiveDate]);

  return (
    <WrappedSection withBorder={withBorder} spacerSize={withVerticalSpacing ? 3 : 12}>
      <TypeWrapper variant="center">
        {/* @ts-expect-error - Automatic, Please fix when editing this file */}
        <div className={classNames({ [styles.verticalSpacingWrapper]: withVerticalSpacing })}>
          <div className={classNames(styles.horizontalSpacingWrapper, styles.spacingWrapper)}>
            {hasHeaderDetails && (
              <SpacerWrapper variant="marginBottomSpacer6">
                {image && <Image alt={image.alt} src={image.src} className={styles.image} />}
                <H2>{title}</H2>
                {subtitle && (
                  <Hidden xs sm>
                    {subtitle}
                  </Hidden>
                )}
              </SpacerWrapper>
            )}
            {/* @ts-expect-error - Automatic, Please fix when editing this file */}
            <PremiumReviewToggle type="alternativePage" showBackground={false} />
            {/* @ts-expect-error - Automatic, Please fix when editing this file */}
            <ProductTiles
              {...{ customer, isLoadingPrice, prices, products: productsToLoad, useFutureEffectiveDate }}
              withHorizontalScroll={withHorizontalScroll}
            />
            <Button
              // @ts-expect-error - Automatic, Please fix when editing this file
              className={classNames({ [styles.withNeedsLink]: withNeedsLink })}
              to={compareButtonToLink}
              variant={[styles.compareButton, 'border', 'white']}
            >
              {compareButtonText}
            </Button>
            {withNeedsLink && (
              <StyledNeedsCTA variant="marginBottomSpacer8">
                Not sure what you need?{' '}
                <Clickable to={constants.URLS.SALES.FIND_HEALTH_INSURANCE}>
                  <Strong>Tailor your health cover</Strong>
                </Clickable>
              </StyledNeedsCTA>
            )}
            <div>
              <PriceDisclaimer bold showRefineLink highlightIncome forProducts={productsToLoad} center />
            </div>
          </div>
        </div>
      </TypeWrapper>
      {withDrawer && <PricingDrawer isProductWithHospitalServices showIncomeOptions />}
    </WrappedSection>
  );
};

ProductsSection.defaultProps = {
  ...productListingCommon.defaultProps,
  data: null,
  image: null,
  isLoadingPrice: false,
  withBorder: false,
  withDrawer: true,
  withHorizontalScroll: false,
  withNeedsLink: false,
  withVerticalSpacing: true,
};

// @ts-expect-error - Automatic, Please fix when editing this file
const mapStateToProps = (state, props) => ({
  customer: selectCustomer(state),
  isLoadingPrice: state.price.loading,
  needsProductType: selectNeedsProductType(state, props),
  needsType: selectNeedsType(state, props),
  prices: state.price.data,
  stateProducts: state.product.data,
  useFutureEffectiveDate: state.price.useFutureEffectiveDate,
});

export default compose(
  NeedsProductDataLoader,
  connect(mapStateToProps, productListingCommon.mapDispatchToProps),
)(ProductsSection);

export { ProductsSection as ProductsSectionForTest };
